<template>
  <div class="pc_mycollection">
    <img @click="toggleOverlay" src="img/btn_mycollection.png" />

    <!-- オーバーレイ -->
    <div v-if="showOverlay" class="overlay" @click="closeOverlay">
      <div class="overlay-content" @click.stop>
        <div class="overlay-header">
          <h2>マイコレクション</h2>
          <button @click="closeOverlay" class="close-button">×</button>
        </div>
        <div class="overlay-body">
          <!-- イベントタイトル -->
          <!-- <div class="event-title-section">
            <h3 class="event-title">{{ eventTitle }}</h3>
          </div> -->

          <!-- ローディング表示 -->
          <div v-if="isLoading" class="loading-container">
            <div class="loading-spinner"></div>
            <p class="loading-text">{{ loadingMessage }}</p>
          </div>

          <!-- 商品コレクション -->
          <div v-else-if="products.length > 0" class="products-grid">
            <div
              v-for="product in products"
              :key="product.id"
              class="product-card"
              :class="{
                'has-quantity': product.quantity > 0,
                'no-quantity': product.quantity === 0,
              }"
            >
              <div class="product-image-container">
                <img
                  :src="product.product_image"
                  :alt="product.product_name"
                  class="product-image"
                  :class="{ 'grayed-out': product.quantity === 0 }"
                />
                <div class="quantity-badge" :class="{ 'has-quantity': product.quantity > 0 }">
                  {{ product.quantity }}
                </div>
              </div>
              <div class="product-name" :class="{ 'grayed-out': product.quantity === 0 }">
                {{ product.product_name }}
              </div>
              <!-- <div class="product-rank">{{ product.product_rank_ja }}</div> -->
            </div>
          </div>

          <!-- データなしの場合（商品データが取得できない場合のみ） -->
          <div v-else class="no-data">
            <p>商品データを読み込めませんでした</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from "vue";
import { useRoute } from "vue-router";
import { collection, getDocs, getDoc, doc, onSnapshot, query } from "firebase/firestore";
import db from "../../main.js";

const route = useRoute();

// オーバーレイの表示状態
const showOverlay = ref(false);

// イベントデータ
const eventId = ref(null);
const eventTitle = ref("イベント情報を読み込み中...");
const products = ref([]);
const isLoading = ref(false);
const loadingMessage = ref("データを読み込み中...");

// データキャッシュ
const isDataLoaded = ref(false);
const cachedEventTitle = ref("");
const cachedProducts = ref([]);
const orderIds = ref([]);

// リアルタイムリスナー
let unsubscribeOrders = null;
let unsubscribeProducts = null;

// ユーザー情報（propsから取得）
const props = defineProps({
  user: Object,
});

const toggleOverlay = () => {
  showOverlay.value = !showOverlay.value;
  if (showOverlay.value) {
    // 背景のスクロールを禁止
    document.body.style.overflow = "hidden";

    // URLからevent_idを取得
    const currentEventId = route.query.event_id;
    if (currentEventId) {
      // イベントIDが変更された場合のみデータを再取得
      if (eventId.value !== currentEventId) {
        eventId.value = currentEventId;
        // 既存のリスナーをクリーンアップ
        cleanupListeners();
        // 新しいデータを取得
        fetchEventCollection();
      } else if (isDataLoaded.value) {
        // データが既に読み込まれている場合はキャッシュから復元
        eventTitle.value = cachedEventTitle.value;
        products.value = [...cachedProducts.value];
        isLoading.value = false;
      } else {
        // 初回読み込み
        fetchEventCollection();
      }
    } else {
      console.log("No event_id found in URL");
      // event_idがない場合はローディング状態をリセット
      isLoading.value = false;
      isDataLoaded.value = true;
    }
  } else {
    // オーバーレイを閉じる時にスクロールを復元
    document.body.style.overflow = "";
  }
};

const closeOverlay = () => {
  showOverlay.value = false;
  // オーバーレイを閉じる時にスクロールを復元
  document.body.style.overflow = "";
};

// リスナーのクリーンアップ
const cleanupListeners = () => {
  if (unsubscribeOrders) {
    unsubscribeOrders();
    unsubscribeOrders = null;
  }
  if (unsubscribeProducts) {
    unsubscribeProducts();
    unsubscribeProducts = null;
  }
};

// 特定のイベントのコレクションデータを取得
const fetchEventCollection = async () => {
  if (!props.user?.uid || !eventId.value) {
    console.log("User UID or Event ID not available");
    return;
  }

  // データ取得状態をリセット
  isDataLoaded.value = false;
  isLoading.value = true;
  loadingMessage.value = "コレクションデータを読み込み中...";

  try {
    console.log("Fetching collection for eventId:", eventId.value);

    // 1. イベント情報を取得（一度だけ）
    if (!cachedEventTitle.value) {
      await fetchEventTitle();
    } else {
      eventTitle.value = cachedEventTitle.value;
    }

    // 2. リアルタイムリスナーを設定（初回データ取得までローディングを維持）
    setupRealtimeListeners();

    // ローディングは setupRealtimeListeners 内の checkInitialDataLoaded で false になる
  } catch (error) {
    console.error("Error fetching event collection:", error);
    // エラー時は即座にローディングを終了
    isLoading.value = false;
    isDataLoaded.value = true;
  }
};

// リアルタイムリスナーを設定
const setupRealtimeListeners = () => {
  if (!props.user?.uid || !eventId.value) return;

  // 初回データ取得完了フラグ
  let initialOrdersLoaded = false;
  let initialProductsLoaded = false;

  // 注文のリアルタイムリスナー
  const ordersQuery = query(collection(db, "users", props.user.uid, "Goods_productorders"));

  unsubscribeOrders = onSnapshot(
    ordersQuery,
    async (snapshot) => {
      console.log("Orders updated, processing...");

      const foundOrderIds = [];
      const changes = snapshot.docChanges();

      // 初回データ取得時はローディングメッセージを更新
      if (isLoading.value && !isDataLoaded.value) {
        loadingMessage.value = "注文データを読み込み中...";
      }

      // 初回データ取得時は必ず処理を実行
      if (!initialOrdersLoaded || changes.length > 0) {
        for (const docSnapshot of snapshot.docs) {
          const orderId = docSnapshot.id;
          try {
            const orderDocRef = doc(db, "Goods_productorders", orderId);
            const orderDocSnap = await getDoc(orderDocRef);

            if (orderDocSnap.exists()) {
              const orderData = orderDocSnap.data();
              if (orderData.event_id === eventId.value) {
                foundOrderIds.push(orderId);
              }
            }
          } catch (error) {
            console.error(`Order document取得に失敗しました (orderId: ${orderId}):`, error);
          }
        }

        orderIds.value = foundOrderIds;
        await updateProductsFromOrders(foundOrderIds);

        // 初回データ取得完了をマーク
        if (!initialOrdersLoaded) {
          initialOrdersLoaded = true;
          checkInitialDataLoaded();
        }
      }
    },
    (error) => {
      console.error("Orders listener error:", error);
      // エラー時も初回データ取得完了をマーク
      if (!initialOrdersLoaded) {
        initialOrdersLoaded = true;
        checkInitialDataLoaded();
      }
    }
  );

  // 商品データのリアルタイムリスナー
  const productsQuery = query(collection(db, "Goods_products", eventId.value, "products"));

  unsubscribeProducts = onSnapshot(
    productsQuery,
    async (snapshot) => {
      console.log("Products updated, processing...");

      // 初回データ取得時はローディングメッセージを更新
      if (isLoading.value && !isDataLoaded.value) {
        loadingMessage.value = "データを読み込み中...";
      }

      const changes = snapshot.docChanges();
      // 初回データ取得時は必ず処理を実行
      if (!initialProductsLoaded || changes.length > 0) {
        await updateProductsFromOrders(orderIds.value);

        // 初回データ取得完了をマーク
        if (!initialProductsLoaded) {
          initialProductsLoaded = true;
          checkInitialDataLoaded();
        }
      }
    },
    (error) => {
      console.error("Products listener error:", error);
      // エラー時も初回データ取得完了をマーク
      if (!initialProductsLoaded) {
        initialProductsLoaded = true;
        checkInitialDataLoaded();
      }
    }
  );

  // 初回データ取得完了をチェック
  const checkInitialDataLoaded = () => {
    if (initialOrdersLoaded && initialProductsLoaded && isLoading.value) {
      // 少し遅延を入れてローディング完了を確実にする
      setTimeout(() => {
        if (isLoading.value) {
          isLoading.value = false;
        }
      }, 500);
    }
  };
};

// イベントタイトルを取得
const fetchEventTitle = async () => {
  if (!eventId.value) return;

  try {
    const eventDocRef = doc(db, "Goods_products", eventId.value);
    const eventDocSnap = await getDoc(eventDocRef);

    if (eventDocSnap.exists()) {
      const eventData = eventDocSnap.data();
      const title =
        eventData.eventtitle || `${eventData.vendorname_ja || "イベント"}限定グッズ販売`;
      eventTitle.value = title;
      cachedEventTitle.value = title; // キャッシュに保存
    } else {
      eventTitle.value = "イベント情報が見つかりません";
      cachedEventTitle.value = "イベント情報が見つかりません";
    }
  } catch (error) {
    console.error("Error fetching event title:", error);
    eventTitle.value = "イベント情報の取得に失敗しました";
    cachedEventTitle.value = "イベント情報の取得に失敗しました";
  }
};

// 注文データから商品を更新
const updateProductsFromOrders = async (orderIdsList) => {
  try {
    // 初回データ取得時はローディングメッセージを更新
    if (isLoading.value && !isDataLoaded.value) {
      loadingMessage.value = "データを読み込み中......";
    }

    // 1. 商品データを取得
    const productsSubcollectionRef = collection(db, "Goods_products", eventId.value, "products");
    const productsSnapshot = await getDocs(productsSubcollectionRef);

    let allEventProducts = [];

    if (productsSnapshot.docs.length > 0) {
      productsSnapshot.forEach((doc) => {
        const productData = doc.data();
        allEventProducts.push({
          productid: doc.id,
          product_image: productData.product_image || "/img/comment_card.png",
          product_name: productData.product_name || "商品名不明",
          product_rank: productData.product_rank || "b",
          product_rank_ja: productData.product_rank_ja || "B賞",
        });
      });
    }

    // 2. 注文された商品の数量を集計
    const orderedProductMap = new Map();

    for (const orderId of orderIdsList) {
      try {
        const productsDocRef = doc(db, "Goods_productorders", orderId, "orderproducts", "products");
        const productsDocSnap = await getDoc(productsDocRef);

        if (productsDocSnap.exists()) {
          const productsData = productsDocSnap.data();
          if (productsData.products && Array.isArray(productsData.products)) {
            productsData.products.forEach((productData) => {
              const productid = productData.productid;
              if (orderedProductMap.has(productid)) {
                orderedProductMap.set(productid, orderedProductMap.get(productid) + 1);
              } else {
                orderedProductMap.set(productid, 1);
              }
            });
          }
        }
      } catch (error) {
        console.error(`Order products取得に失敗しました (orderId: ${orderId}):`, error);
      }
    }

    // 3. 全商品を処理
    const allProducts = allEventProducts.map((productData, index) => {
      const productid = productData.productid;
      const orderedQuantity = orderedProductMap.get(productid) || 0;

      return {
        id: `event_${index}`,
        productid: productid,
        product_image: productData.product_image,
        product_name: productData.product_name,
        product_rank: productData.product_rank,
        product_rank_ja: productData.product_rank_ja,
        quantity: orderedQuantity,
      };
    });

    // 4. ランク順でソート
    const baseRankOrder = { ssr: 1, r: 2, a: 3, b: 4, c: 5 };
    allProducts.sort((a, b) => {
      const rankA = baseRankOrder[a.product_rank] || 999;
      const rankB = baseRankOrder[b.product_rank] || 999;
      if (rankA !== rankB) {
        return rankA - rankB;
      }
      const productIdA = parseInt(a.productid, 10) || 0;
      const productIdB = parseInt(b.productid, 10) || 0;
      return productIdA - productIdB;
    });

    products.value = allProducts;
    cachedProducts.value = [...allProducts]; // キャッシュに保存
    isDataLoaded.value = true;

    // 初回データ取得完了時はローディング状態をここでは変更しない
    // setupRealtimeListeners内のcheckInitialDataLoadedで管理する
  } catch (error) {
    console.error("Error updating products from orders:", error);
    // エラー時もデータ取得完了としてマーク
    isDataLoaded.value = true;
  }
};

onMounted(() => {
  // コンポーネントマウント時は何もしない（オーバーレイが開かれた時のみデータ取得）
});

onUnmounted(() => {
  // コンポーネント破棄時にリスナーをクリーンアップ
  cleanupListeners();
  // スクロールを復元
  document.body.style.overflow = "";
});
</script>

<style scoped>
.pc_mycollection {
  position: absolute;
  top: 235px;
  right: 18px;
  width: 30px;
  z-index: 1000;
}
.pc_mycollection img {
  width: 120%;
  z-index: 1000;
  filter: drop-shadow(2px 2px 5px rgba(0, 0, 0, 0.5));
  cursor: pointer;
}

/* オーバーレイスタイル */
.overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  z-index: 99999;
  display: flex;
  justify-content: center;
  align-items: center;
}

.overlay-content {
  background-color: white;
  border-radius: 10px;
  padding: 20px;
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.overlay-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 1px solid #eee;
}

.overlay-header h2 {
  margin: 0;
  color: #333;
  font-size: 24px;
  font-weight: bold;
}

.close-button {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s;
}

.close-button:hover {
  background-color: #f0f0f0;
}

.overlay-body {
  color: #666;
  line-height: 1.6;
  max-height: 60vh;
  overflow-y: auto;
}

/* ローディング表示 */
.loading-container {
  text-align: center;
  padding: 40px 20px;
}

.loading-spinner {
  width: 30px;
  height: 30px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #ff69b4;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 15px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.loading-text {
  font-size: 14px;
  color: #666;
  margin: 0;
}

/* イベントタイトルセクション */
.event-title-section {
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid #eee;
}

.event-title-section .event-title {
  font-size: 18px;
  font-weight: bold;
  color: #333;
  margin: 0;
  text-align: center;
}

/* 商品グリッド */
.products-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 15px;
  max-height: 50vh;
  overflow-y: auto;
  padding: 10px 0;
}

.product-card {
  background-color: white;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.2s ease;
  position: relative;
  cursor: pointer;
}

.product-card.has-quantity {
  border-color: #ff69b4;
}

.product-card.no-quantity {
  border-color: #e0e0e0;
  opacity: 0.6;
}

.product-card:hover {
  transform: translateY(-2px);
}

.product-image-container {
  position: relative;
  width: 100%;
  height: 140px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f8f9fa;
}

.product-image {
  width: 100px !important;
  height: 100px !important;
  object-fit: contain;
  max-width: none;
  max-height: none;
  box-shadow: none;
  filter: none;
  transition: filter 0.3s ease;
}

.product-image.grayed-out {
  filter: grayscale(100%) brightness(0.7);
}

.quantity-badge {
  position: absolute;
  top: 8px;
  right: 8px;
  background-color: #e0e0e0;
  color: #000;
  border-radius: 12px;
  padding: 2px 6px;
  font-size: 10px;
  font-weight: bold;
  min-width: 16px;
  text-align: center;
}

.quantity-badge.has-quantity {
  background-color: #ff69b4;
  color: white;
}

.product-name {
  padding: 8px 6px;
  font-size: 10px;
  font-weight: bold;
  color: #000;
  text-align: center;
  background-color: white;
  line-height: 1.2;
  transition: color 0.3s ease;
}

.product-name.grayed-out {
  color: #999;
}

.product-rank {
  padding: 4px 6px;
  font-size: 9px;
  color: #666;
  text-align: center;
  background-color: #f8f9fa;
  border-top: 1px solid #eee;
}

/* データなしの場合 */
.no-data {
  text-align: center;
  padding: 40px 20px;
  color: #999;
}

.no-data p {
  margin: 0 0 10px 0;
  font-size: 16px;
}

.no-data-sub {
  font-size: 14px !important;
  color: #ccc !important;
}

/* レスポンシブデザイン */
@media (max-width: 768px) {
  .products-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }

  .product-image-container {
    height: 100px;
  }

  .product-image {
    width: 60px !important;
    height: 60px !important;
  }

  .product-image.grayed-out {
    filter: grayscale(100%) brightness(0.7);
  }
}

@media (max-width: 480px) {
  .products-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
  }

  .product-image-container {
    height: 80px;
  }

  .product-image {
    width: 50px !important;
    height: 50px !important;
  }

  .product-image.grayed-out {
    filter: grayscale(100%) brightness(0.7);
  }

  .product-name {
    font-size: 9px;
    padding: 6px 4px;
  }

  .product-rank {
    font-size: 8px;
    padding: 3px 4px;
  }
}

@media (min-width: 960px) {
  .pc_mycollection {
    position: absolute;
    top: 165px;
    right: 18px;
    width: 30px;
    z-index: 1;
  }
  .pc_mycollection img {
    width: 120%;
    z-index: 1000;
    filter: drop-shadow(2px 2px 5px rgba(0, 0, 0, 0.5));
  }
}
</style>
